import {
    Button,
    Modal,
    PropagationNotice,
    TextAreaField,
    TextField,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';

import { useSetOverride } from '../hooks/use-flags';

/**
 * Override one museum's value for this flag.
 *
 * ## Why the museum is an id typed by hand
 *
 * Because there is nothing to pick from yet. Overrides carry a bare `tenantId`,
 * and rendering "The Broad" instead of `00Dxx0000001aBcD` needs a tenants
 * service this app does not talk to — it is listed as open in the module README.
 * A fake picker over invented names would be worse than an honest text field:
 * the operator would trust the label and have no way to check it.
 *
 * A mistyped id is not destructive, which is what makes the plain field
 * acceptable in the meantime — the row addresses a museum that does not exist,
 * resolves for nobody, and can be reverted. That is a materially smaller blast
 * radius than a mistyped *flag key*, which is why that one gets a confirm step
 * and this does not.
 */
export function OverrideDialog({
    flagKey,
    open,
    onClose,
}: {
    flagKey: string;
    open: boolean;
    onClose: () => void;
}) {
    const [tenantId, setTenantId] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [rollout, setRollout] = useState('');
    const [reason, setReason] = useState('');
    const setOverride = useSetOverride(flagKey);

    const close = () => {
        setTenantId('');
        setEnabled(true);
        setRollout('');
        setReason('');
        setOverride.reset();
        onClose();
    };

    const trimmedRollout = rollout.trim();
    const rolloutValue = trimmedRollout.length === 0 ? undefined : Number(trimmedRollout);
    const rolloutError =
        rolloutValue !== undefined &&
        (!Number.isInteger(rolloutValue) || rolloutValue < 0 || rolloutValue > 100)
            ? 'Enter a whole number from 0 to 100, or leave it blank for no gate.'
            : undefined;

    const complete = tenantId.trim().length > 0 && rolloutError === undefined;

    return (
        <Modal
            open={open}
            onClose={close}
            dismissable={!setOverride.isPending}
            title="Override a museum"
            description={`Replaces the platform default of ${flagKey} for one museum. Overrides are not versioned — the last write wins.`}
            footer={
                <>
                    <Button variant="ghost" onClick={close} disabled={setOverride.isPending}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!complete || setOverride.isPending}
                        onClick={() => {
                            setOverride.mutate(
                                {
                                    tenantId: tenantId.trim(),
                                    enabled,
                                    ...(rolloutValue !== undefined
                                        ? { rolloutPercentage: rolloutValue }
                                        : {}),
                                    ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
                                },
                                { onSuccess: close },
                            );
                        }}
                    >
                        {setOverride.isPending ? 'Saving…' : 'Save override'}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                <TextField
                    label="Museum (tenant id)"
                    value={tenantId}
                    onChange={setTenantId}
                    placeholder="00Dxx0000001aBcD"
                    mono
                    autoFocus
                    hint="The Salesforce org id. Names are not resolvable here yet — the Backoffice does not read the tenants service."
                />

                <div className="flex flex-col gap-1.5">
                    <span className="text-[12.5px] font-semibold text-gray-700">Value</span>
                    <div className="flex items-center gap-3">
                        <Toggle
                            on={enabled}
                            onChange={setEnabled}
                            showLabel
                            aria-label="Override value"
                        />
                        <span className="text-[12.5px] text-gray-500">
                            What this museum gets instead of the platform default.
                        </span>
                    </div>
                </div>

                <TextField
                    label="Rollout % (optional)"
                    value={rollout}
                    onChange={setRollout}
                    placeholder="Leave blank for no gate"
                    error={rolloutError}
                    hint="0 means off for everyone — it is not the same as blank."
                />

                <TextAreaField
                    label="Reason (optional)"
                    value={reason}
                    onChange={setReason}
                    rows={2}
                    placeholder="Why this museum differs"
                    hint="Recorded on the audit entry."
                />

                {setOverride.error && <WriteError error={setOverride.error} />}

                <PropagationNotice />
            </div>
        </Modal>
    );
}
